import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useIsInViewport, usePhoneMode } from "../../hooks";
import { useRecentServiceLog } from "../../fetch/fetchMaintenance";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import { useNavigate } from "react-router-dom";
import Spinner from "../spinner/Spinner";
import AddIcon from '@mui/icons-material/Add';
import { logFeatureUsage } from "../../fetch/insightsHelper";
import { useFetchDealerBasic, useFetchProfile } from "../../fetch/userFetch";

dayjs.extend(utc);

export default function RecentServiceLog() {
    const navigate = useNavigate();
    const [showAddModal, setShowAddModal] = useState(false);
    const { phoneMode, isMobile } = usePhoneMode();
    const theme = useTheme();
    const scrollViewerTop = theme.appBar.height;

    const { data: profileData } = useFetchProfile(true);

    useEffect(() => {
        logFeatureUsage('RecentServiceLog');
        document.title = 'Recent Service Logs';
    }, []);

    const { items, totalRecords, isLoading, isValidating, size, setSize, error } = useRecentServiceLog({ pageSize: 10 })
    const loadMoreRef = useRef(null);
    const [isInViewport1, _intersectionRatio] = useIsInViewport(loadMoreRef);
    useEffect(() => {
        if (isInViewport1 && !isLoading && !isValidating && items.length < totalRecords) {
            setTimeout(() => {
                setSize(size + 1);
            }, 250);
        }
    }, [isInViewport1, isLoading, isValidating, items.length, totalRecords]);

    const showDealer = items.filter(it => it.dealerNumber != profileData?.accountNumber).length > 0;

    return (
        <Grid
            id='scroll-viewer'
            container
            style={{
                position: 'fixed',
                top: scrollViewerTop,
                left: 0,
                height: `calc(100vh - ${scrollViewerTop}px)`,
                overflowX: 'hidden',
                overflowY: 'hidden',
                width: '100vw',
                padding: '1.5em 1em 1.5em 1em'
            }}
        >
            <Grid item xs>
                <Grid container columns={{ xs: 1 }} spacing={2}>
                    <Paper
                        sx={{
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: 'calc(100% - 1em)',
                            marginLeft: '1em',
                            marginTop: '.5em',
                            marginBottom: '1em',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            position: 'relative'
                        }}
                    >
                        <Dialog onClose={() => setShowAddModal(false)} open={showAddModal}>
                            <DialogTitle>Add Service Log</DialogTitle>
                            <DialogContent>
                                Do you want to add a new service log from scratch or import from recent scan?
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => navigate('/literature/service/snapshots')}>From Recent Scan</Button>
                                <Button onClick={() => navigate('/literature/service/log/add')}>New</Button>
                            </DialogActions>
                        </Dialog>
                        <div style={{ width: '100%' }}>
                            <Button onClick={() => setShowAddModal(true)}><AddIcon />Add</Button>
                        </div>
                        <TableContainer style={{ maxHeight: `calc(100vh - ${scrollViewerTop + (isMobile ? 36 : 0)}px - 3.5em)` }}>
                            <Table stickyHeader size={phoneMode ? "small" : "medium"}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            Scan Date
                                        </TableCell>
                                        {showDealer ? <TableCell>
                                            Dealer
                                        </TableCell> : null}
                                        <TableCell>
                                            Hull Id
                                        </TableCell>
                                        <TableCell>
                                            Engine Serial(s)
                                        </TableCell>
                                        <TableCell>
                                            Service Type
                                        </TableCell>
                                        <TableCell>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                Loading...
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        items && items.map((item) => {
                                            return (
                                                <TableRow key={item.id}>
                                                    <TableCell>{dayjs(item.dateofService).format('L')}</TableCell>
                                                    {showDealer ? <TableCell>
                                                        <DealerNameLookup dealerNumber={item.dealerNumber} />
                                                    </TableCell> : null}
                                                    <TableCell>{item.hullId}</TableCell>
                                                    <TableCell>{item.engineSerials}</TableCell>
                                                    <TableCell>
                                                        {item.serviceType}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button onClick={() => navigate(`/literature/service/log/${item.id}`)}>{item.isEditable ? 'Edit' : 'View'}</Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                    {items.length === 0 && !isLoading && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">No records found.</TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow style={{ display: items.length < totalRecords ? null : 'none' }} ref={loadMoreRef}>
                                        <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                                            {!isLoading && !isValidating ? (
                                                <Button
                                                    onClick={() => setSize(size + 1)}
                                                >
                                                    Load Next
                                                </Button>
                                            ) : (
                                                <Spinner width={35} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Grid>
    );
}

function DealerNameLookup({ dealerNumber }) {
    const { data: dealerInfo } = useFetchDealerBasic(dealerNumber);

    return dealerInfo?.name || '';
}